import { TeamsIcon } from "@/components/icons/TeamsIcon.tsx";
import { SlackIcon } from "@/components/icons/SlackIcon.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import type { ThreadChannel } from "@/types/thread.ts";
import { MessageCircle } from "lucide-react";

/**
 * Copy + icon for the composer notice, keyed by messaging channel. `app` and
 * `mcp` have no entry, so the notice renders nothing for them.
 */
const noticeByChannel: Partial<
  Record<ThreadChannel, { icon: React.FC<{ className?: string }>; text: string }>
> = {
  text_message: {
    icon: MessageCircle,
    text: "Messages you send here are delivered to this text conversation.",
  },
  slack: {
    icon: SlackIcon,
    text: "Messages you send here are delivered to this Slack conversation.",
  },
  teams: {
    icon: TeamsIcon,
    text: "Messages you send here are delivered to this Microsoft Teams conversation.",
  },
  whatsapp: {
    icon: WhatsAppIcon,
    text: "Messages you send here are delivered to this WhatsApp conversation.",
  },
};

/**
 * Small notice shown above the composer on a thread bridged to an external
 * messaging channel, so the user knows a reply typed here goes out to that
 * conversation (and, in a group, is seen by everyone on it). Renders nothing
 * for in-app and programmatic threads.
 */
export const ExternalChannelComposerNotice = ({ channel }: { channel: ThreadChannel }) => {
  const notice = noticeByChannel[channel];
  if (!notice) return null;

  const { icon: Icon, text } = notice;
  return (
    <div className="flex justify-center pointer-events-auto mb-2">
      <div className="flex items-center gap-1.5 w-[688px] max-w-full px-3 text-xs text-muted-foreground">
        <Icon className="size-3.5 shrink-0" />
        <span>{text}</span>
      </div>
    </div>
  );
};
